import * as React from 'react'
import PropTypes from 'prop-types'
import { useTheme } from '@mui/material/styles'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import { LineChart } from '@mui/x-charts/LineChart'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat'
import InfoIcon from '@mui/icons-material/Info'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import { useMediaQuery } from '@mui/material'

function AreaGradient({ color, id }) {
	return (
		<defs>
			<linearGradient id={id} x1="50%" y1="0%" x2="50%" y2="100%">
				<stop offset="0%" stopColor={color} stopOpacity={0.6} />
				<stop offset="50%" stopColor={color} stopOpacity={0.3} />
				<stop offset="100%" stopColor={color} stopOpacity={0.1} />
			</linearGradient>
		</defs>
	)
}

AreaGradient.propTypes = {
	color: PropTypes.string.isRequired,
	id: PropTypes.string.isRequired,
}

function getDaysInMonth(month, year) {
	const date = new Date(year, month, 0)
	const monthName = date.toLocaleDateString('en-US', {
		month: 'short',
	})
	const daysInMonth = date.getDate()
	const days = []
	let i = 1
	while (days.length < daysInMonth) {
		days.push(`${monthName} ${i}`)
		i += 1
	}
	return days
}

function getMonthText(monthNumber) {
	const monthNames = [
		'January', 'February', 'March', 'April', 'May', 'June',
		'July', 'August', 'September', 'October', 'November', 'December',
	]
	return monthNames[monthNumber - 1] || 'Unknown'
}

function getLastMonthPercentageChange(monthlySadhnaCounts) {
	if (!monthlySadhnaCounts || monthlySadhnaCounts.length < 3) return null

	const current = monthlySadhnaCounts[monthlySadhnaCounts.length - 2]?.totalCount || 0
	const previous = monthlySadhnaCounts[monthlySadhnaCounts.length - 3]?.totalCount || 0

	if (previous === 0) return null

	const percentage = ((current - previous) / previous) * 100
	return {
		value: Math.abs(percentage).toFixed(1),
		isPositive: percentage >= 0,
		trend: percentage > 0 ? 'up' : percentage < 0 ? 'down' : 'neutral'
	}
}

function getTrendIcon(trend) {
	switch (trend) {
		case 'up':
			return <TrendingUpIcon sx={{ color: 'success.main', fontSize: '1.2rem' }} />
		case 'down':
			return <TrendingDownIcon sx={{ color: 'error.main', fontSize: '1.2rem' }} />
		default:
			return <TrendingFlatIcon sx={{ color: 'text.disabled', fontSize: '1.2rem' }} />
	}
}

export default function MonthlySadhnaChart({ monthlySadhnaCounts = [] }) {
	const theme = useTheme()
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
	const isTablet = useMediaQuery(theme.breakpoints.down('md'))

	// Enhanced data processing
	const processedData = React.useMemo(() => {
		if (!monthlySadhnaCounts || monthlySadhnaCounts.length === 0) return []

		return monthlySadhnaCounts.map((item, index) => ({
			...item,
			formattedMonth: `${getMonthText(item.month)} ${item.year}`,
			shortMonth: getMonthText(item.month).substring(0, 3),
			index
		}))
	}, [monthlySadhnaCounts])

	const xAxisData = processedData.map(item =>
		isMobile ? item.shortMonth : item.formattedMonth
	)

	const colorPalette = [
		theme.palette.primary.light,
		theme.palette.primary.main,
		theme.palette.primary.dark,
	]

	// Calculate statistics
	const currentMonthData = processedData[processedData.length - 2]
	const previousMonthData = processedData[processedData.length - 3]
	const percentageChange = getLastMonthPercentageChange(monthlySadhnaCounts)

	// Calculate average and total
	const totalSadhna = processedData.reduce((sum, item) => sum + (item.totalCount || 0), 0)
	const averageSadhna = processedData.length > 0 ? Math.round(totalSadhna / processedData.length) : 0

	if (processedData.length === 0) {
		return (
			<Card
				variant="outlined"
				sx={{
					width: '100%',
					borderRadius: { xs: '16px', sm: '12px' },
					boxShadow: { xs: '0 4px 20px rgba(0,0,0,0.08)', sm: '0 2px 8px rgba(0,0,0,0.06)' },
					border: 'none',
					background: theme.palette.mode === 'light'
						? 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
						: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
				}}
			>
				<CardContent sx={{
					padding: { xs: 2, sm: 2.5 },
					textAlign: 'center',
					minHeight: '300px',
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'center',
					alignItems: 'center'
				}}>
					<CalendarMonthIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
					<Typography variant="h6" color="text.secondary" gutterBottom>
						No Data Available
					</Typography>
					<Typography variant="body2" color="text.disabled">
						Monthly Sadhna data will appear here
					</Typography>
				</CardContent>
			</Card>
		)
	}

	return (
		<Card
			variant="outlined"
			sx={{
				width: '100%',
				borderRadius: { xs: '16px', sm: '12px' },
				boxShadow: { xs: '0 4px 20px rgba(0,0,0,0.08)', sm: '0 2px 8px rgba(0,0,0,0.06)' },
				border: 'none',
				background: theme.palette.mode === 'light'
					? 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
					: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
				transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
				position: 'relative',
				overflow: 'hidden',
				'&:hover': {
					transform: { xs: 'none', sm: 'translateY(-2px)' },
					boxShadow: { xs: '0 8px 30px rgba(0,0,0,0.12)', sm: '0 8px 25px rgba(0,0,0,0.1)' },
				},
				'&::before': {
					content: '""',
					position: 'absolute',
					top: 0,
					left: 0,
					right: 0,
					height: '4px',
					background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
				}
			}}
		>
			<CardContent sx={{
				padding: { xs: 2, sm: 2.5 },
				'&:last-child': { paddingBottom: { xs: 2, sm: 2.5 } },
				position: 'relative'
			}}>
				{/* Header Section */}
				<Stack
					direction="row"
					justifyContent="space-between"
					alignItems="flex-start"
					sx={{ mb: 3 }}
				>
					<Box sx={{ flex: 1 }}>
						<Typography
							component="h2"
							variant="h6"
							sx={{
								fontSize: { xs: '1.1rem', sm: '1.25rem' },
								fontWeight: 600,
								color: 'text.primary',
								lineHeight: 1.3,
								mb: 0.5,
								display: 'flex',
								alignItems: 'center',
								gap: 1
							}}
						>
							<CalendarMonthIcon sx={{ fontSize: '1.2em', color: 'primary.main' }} />
							Monthly Sadhna Updates
						</Typography>
						<Typography
							variant="body2"
							sx={{
								color: 'text.secondary',
								fontSize: { xs: '0.875rem', sm: '1rem' }
							}}
						>
							Track your spiritual progress over time
						</Typography>
					</Box>

					{/* Info Tooltip */}
					<Tooltip
						title="Monthly Sadhna tracking and analytics"
						placement="top"
						arrow
					>
						<IconButton
							size="small"
							sx={{
								color: 'text.disabled',
								'&:hover': {
									color: 'text.secondary',
									backgroundColor: 'action.hover'
								}
							}}
						>
							<InfoIcon fontSize="small" />
						</IconButton>
					</Tooltip>
				</Stack>

				{/* Statistics Section */}
				<Stack
					direction={{ xs: 'column', sm: 'row' }}
					spacing={{ xs: 2, sm: 3 }}
					sx={{ mb: 3 }}
				>
					{/* Current Month */}
					<Box sx={{
						flex: 1,
						textAlign: { xs: 'center', sm: 'left' }
					}}>
						<Stack
							direction="row"
							alignItems="center"
							justifyContent={{ xs: 'center', sm: 'flex-start' }}
							spacing={1}
							sx={{ mb: 1 }}
						>
							<Typography
								variant="h4"
								component="p"
								sx={{
									fontSize: { xs: '2rem', sm: '2.5rem' },
									fontWeight: 700,
									color: 'text.primary',
									lineHeight: 1.1
								}}
							>
								{currentMonthData?.totalCount || 0}
							</Typography>
							{percentageChange && (
								<Chip
									icon={getTrendIcon(percentageChange.trend)}
									size="small"
									color={percentageChange.isPositive ? 'success' : 'error'}
									label={`${percentageChange.isPositive ? '+' : '-'}${percentageChange.value}%`}
									variant="outlined"
									sx={{
										fontWeight: 600,
										fontSize: { xs: '0.75rem', sm: '0.8rem' },
										borderRadius: '20px',
										'& .MuiChip-icon': {
											fontSize: '1rem'
										}
									}}
								/>
							)}
						</Stack>
						<Typography
							variant="body2"
							sx={{
								color: 'text.secondary',
								fontSize: { xs: '0.875rem', sm: '1rem' }
							}}
						>
							Current month
						</Typography>
					</Box>

					{/* Previous Month */}
					<Box sx={{
						flex: 1,
						textAlign: { xs: 'center', sm: 'left' }
					}}>
						<Typography
							variant="h5"
							component="p"
							sx={{
								fontSize: { xs: '1.5rem', sm: '2rem' },
								fontWeight: 600,
								color: 'text.secondary',
								lineHeight: 1.1,
								mb: 1
							}}
						>
							{previousMonthData?.totalCount || 0}
						</Typography>
						<Typography
							variant="body2"
							sx={{
								color: 'text.secondary',
								fontSize: { xs: '0.875rem', sm: '1rem' }
							}}
						>
							Previous month
						</Typography>
					</Box>

					{/* Average */}
					<Box sx={{
						flex: 1,
						textAlign: { xs: 'center', sm: 'left' }
					}}>
						<Typography
							variant="h5"
							component="p"
							sx={{
								fontSize: { xs: '1.5rem', sm: '2rem' },
								fontWeight: 600,
								color: 'text.secondary',
								lineHeight: 1.1,
								mb: 1
							}}
						>
							{averageSadhna}
						</Typography>
						<Typography
							variant="body2"
							sx={{
								color: 'text.secondary',
								fontSize: { xs: '0.875rem', sm: '1rem' }
							}}
						>
							Monthly average
						</Typography>
					</Box>
				</Stack>

				{/* Chart Section */}
				<Box sx={{
					width: '100%',
					height: { xs: '200px', sm: '250px', md: '300px' },
					position: 'relative'
				}}>
					<LineChart
						colors={colorPalette}
						xAxis={[
							{
								scaleType: 'point',
								data: xAxisData,
								tickInterval: (index, i) => {
									// Show fewer labels on mobile
									if (isMobile) return (i + 1) % 3 === 0
									if (isTablet) return (i + 1) % 2 === 0
									return (i + 1) % 1 === 0
								},
								tickLabelStyle: {
									fontSize: isMobile ? 10 : 12,
									fill: theme.palette.text.secondary
								}
							},
						]}
						yAxis={[
							{
								tickLabelStyle: {
									fontSize: isMobile ? 10 : 12,
									fill: theme.palette.text.secondary
								},
								grid: { strokeDasharray: '3 3' }
							}
						]}
						series={[
							{
								id: 'monthlySadhna',
								label: 'Monthly Sadhna Count',
								showMark: true,
								markSize: isMobile ? 4 : 6,
								curve: 'monotoneX',
								area: true,
								data: processedData.map(item => item.totalCount || 0),
								color: theme.palette.primary.main,
							},
						]}
						height={isMobile ? 200 : isTablet ? 250 : 300}
						margin={{
							left: isMobile ? 40 : 50,
							right: isMobile ? 15 : 20,
							top: isMobile ? 15 : 20,
							bottom: isMobile ? 30 : 40
						}}
						grid={{
							horizontal: true,
							vertical: false,
							color: theme.palette.divider
						}}
						sx={{
							'& .MuiAreaElement-series-monthlySadhna': {
								fill: "url('#monthlySadhna')",
							},
							'& .MuiMarkElement-root': {
								fill: theme.palette.primary.main,
								stroke: theme.palette.background.paper,
								strokeWidth: 2
							},
							'& .MuiChartsAxis-line': {
								stroke: theme.palette.divider,
								strokeWidth: 1
							},
							'& .MuiChartsAxis-tick': {
								stroke: theme.palette.divider,
								strokeWidth: 1
							}
						}}
						slotProps={{
							legend: {
								hidden: true,
							},
							tooltip: {
								content: ({ active, payload }) => {
									if (active && payload && payload.length) {
										const data = payload[0].payload
										return (
											<Box sx={{
												backgroundColor: theme.palette.background.paper,
												border: `1px solid ${theme.palette.divider}`,
												borderRadius: 1,
												padding: 1,
												boxShadow: theme.shadows[4]
											}}>
												<Typography variant="body2" fontWeight="bold">
													{data.formattedMonth || data.shortMonth}
												</Typography>
												<Typography variant="body2" color="primary">
													{data.totalCount} Sadhna updates
												</Typography>
											</Box>
										)
									}
									return null
								}
							}
						}}
					>
						<AreaGradient
							color={theme.palette.primary.main}
							id="monthlySadhna"
						/>
					</LineChart>
				</Box>

				{/* Chart Footer */}
				<Box sx={{
					mt: 2,
					textAlign: 'center'
				}}>
					<Typography
						variant="caption"
						sx={{
							color: 'text.disabled',
							fontSize: { xs: '0.75rem', sm: '0.8rem' }
						}}
					>
						{processedData.length} months of data • Total: {totalSadhna} updates
					</Typography>
				</Box>
			</CardContent>
		</Card>
	)
}

MonthlySadhnaChart.propTypes = {
	monthlySadhnaCounts: PropTypes.arrayOf(
		PropTypes.shape({
			month: PropTypes.number.isRequired,
			year: PropTypes.number.isRequired,
			totalCount: PropTypes.number.isRequired,
		})
	),
}
